import React from 'react';
import PropTypes from 'prop-types';
import { Link } from 'gatsby';
import { useSelector } from 'react-redux';

import FacebookIcon from '@mui/icons-material/Facebook';
import TwitterIcon from '@mui/icons-material/Twitter';
import InstagramIcon from '@mui/icons-material/Instagram';

import { CustomizableLink } from '~components/common/Link';
import { CustomizableButton } from '~components/common/Button';
import {
  Accordion,
  AccordionItem,
  AccordionItemButton,
  AccordionItemHeading,
  AccordionItemPanel,
} from 'react-accessible-accordion';
import accordionOpenIconStyling from '~components/common/Accordion/accordionOpenIconStyling';
import ExternalLinkIcon from '../common/ExternalLinkIcon';

import links from './links';

// simple wrapper component to create styled blocks that only highlight on link hover
function FooterLink({ to, text, title = null }) {
  const styles =
    'text-sm text-neutral-content no-underline hover:underline focus:underline border-0';

  if (to.startsWith('http')) {
    // external link
    return (
      <div className='mb-1.5 block text-base'>
        <a
          href={to}
          target='_blank'
          rel='noopener'
          title={title || text}
          className={styles}
        >
          {text}
          {(to.includes('actblue.com') || !to.includes('voteamerica.org')) && (
            <ExternalLinkIcon />
          )}
        </a>
      </div>
    );
  }

  return (
    <div className='mb-1.5 block text-base'>
      <Link to={to} title={text} className={styles}>
        {text}
      </Link>
    </div>
  );
}

FooterLink.propTypes = {
  to: PropTypes.string.isRequired,
  text: PropTypes.string.isRequired,
  title: PropTypes.string,
};

export function EmbedFooter({ sourcing }) {
  return (
    <p className='mb-0 mt-10 text-base'>
      <CustomizableLink
        href={`https://voteamericaplus.org/${sourcing}`}
        target='_blank'
        rel='noopener'
        title='VoteAmerica+ Tools'
      >
        Add this tool to your website
      </CustomizableLink>
    </p>
  );
}

EmbedFooter.propTypes = {
  sourcing: PropTypes.string.isRequired,
};

export function SocialLink({ name, icon, href }) {
  return (
    <a
      className='float-left block border-0 text-base-content'
      href={href}
      target='_blank'
      rel='noopener'
      title={`VoteAmerica ${name}`}
    >
      <div className='w-50 h-50 rounded-full bg-neutral-content p-2.5 transition duration-300 hover:bg-accent'>
        {icon}
        <span className='sr-only'>{name} (Opens in new window)</span>
      </div>
    </a>
  );
}

SocialLink.propTypes = {
  name: PropTypes.string.isRequired,
  icon: PropTypes.object.isRequired,
  href: PropTypes.string.isRequired,
};

function Disclaimer({ paragraphStyles }) {
  const linkStyles = 'text-neutral-content underline border-0';

  return (
    <p className={paragraphStyles}>
      VoteAmerica.org is the joint website of VoteAmerica and VoteAmerica Action
      Fund. VoteAmerica and VoteAmerica Action Fund are separate organizations.
      VoteAmerica is a registered 501(c)(3), and does not support or oppose any
      political candidate or party.{' '}
      <a
        className={linkStyles}
        href='https://secure.actblue.com/donate/voteamerica-website?refcode=www.voteamerica.org'
        title='Donate to VoteAmerica'
        target='_blank'
        rel='noopener'
      >
        Donations to VoteAmerica are tax-deductible
      </a>
      . VoteAmerica Action Fund is a registered 501(c)(4).{' '}
      <a
        className={linkStyles}
        href='https://secure.actblue.com/donate/vaf-2024-website?refcode=www.voteamerica.org'
        title='Donate to VoteAmerica'
        target='_blank'
        rel='noopener'
      >
        Donations to VoteAmerica Action Fund are not tax-deductible
      </a>
      .
    </p>
  );
}

Disclaimer.propTypes = {
  paragraphStyles: PropTypes.string,
};

Disclaimer.defaultProps = {
  paragraphStyles: 'text-base text-base-200',
};

function SiteFooterBottom() {
  return (
    <div className='mt-20 flex flex-col gap-7 md:flex-row md:items-center'>
      <div className='flex flex-row flex-wrap justify-start gap-5 md:basis-[265px]'>
        <SocialLink
          name='Facebook'
          icon={<FacebookIcon alt='Facebook' className='h-[30px] w-[30px]' />}
          href='https://www.facebook.com/VoteAmerica'
        />
        <SocialLink
          name='Twitter'
          icon={<TwitterIcon alt='Twitter' className='h-[30px] w-[30px]' />}
          href='https://twitter.com/voteamerica'
        />
        <SocialLink
          name='Instagram'
          icon={<InstagramIcon alt='Instagram' className='h-[30px] w-[30px]' />}
          href='https://www.instagram.com/voteamerica_'
        />
        <SocialLink
          name='Medium'
          icon={
            <img
              src='/img/medium-icon.svg'
              alt='Medium'
              className='h-[30px] w-[30px]'
            />
          }
          href='https://www.medium.com/voteamerica'
        />
      </div>
      <div className='hidden w-24 md:block'>
        <a
          href='https://trust.voteamerica.org'
          target='_blank'
          rel='noopener'
          title='VoteAmerica trust center'
        >
          <img
            src='/img/SOC2_NonCPA.png'
            alt='SOC2 logo for service organizations'
            className='w-full'
          />
        </a>
      </div>
      <div className='basis-5/12'>
        <Disclaimer paragraphStyles='text-sm text-neutral-content mb-0' />
      </div>
      <div className='basis-3/12'>
        <CustomizableButton
          href='https://secure.actblue.com/donate/voteamerica-website?refcode=www.voteamerica.org'
          target='_blank'
          rel='noopener'
          title='VoteAmerica Donate'
          isCustomizable={false}
          elementType='a'
        >
          Donate
        </CustomizableButton>
      </div>
    </div>
  );
}

function SiteFooterFullMobile() {
  return (
    <div className='block lg:hidden'>
      <Accordion
        allowZeroExpanded
        className='mb-10 border-b border-neutral-content'
      >
        {links.map(({ header, items }) => (
          <AccordionItem
            key={header}
            className='mb-0 border-t border-neutral-content'
          >
            <AccordionItemHeading
              className='bg-none text-neutral-content'
              aria-level='2'
            >
              <AccordionItemButton
                className={`relative px-0 py-4 text-lg font-bold duration-300 after:border-neutral-content hover:cursor-pointer focus:cursor-pointer ${accordionOpenIconStyling}`}
              >
                {header}
              </AccordionItemButton>
            </AccordionItemHeading>
            <AccordionItemPanel className='border-none p-0 duration-300 ease-in [&>*:last-child]:mb-4'>
              {items.map((item) => (
                <FooterLink key={`${item.to}${item.text}`} {...item} />
              ))}
            </AccordionItemPanel>
          </AccordionItem>
        ))}
      </Accordion>
      <SiteFooterBottom />
    </div>
  );
}

function SiteFooterFullDesktop() {
  return (
    <div className='hidden lg:block'>
      <div className='flex flex-row gap-7'>
        {links.map(({ header, items }) => (
          <div key={header} className='flex-1'>
            <h2 className='mb-4 mt-0 text-base uppercase text-neutral-content'>
              {header}
            </h2>
            {items.map((item) => (
              <FooterLink key={`${item.to}${item.text}`} {...item} />
            ))}
          </div>
        ))}
      </div>
      <SiteFooterBottom />
    </div>
  );
}

function SiteFooterFull() {
  return (
    <div className='mx-auto max-w-7xl p-0'>
      <SiteFooterFullDesktop />
      <SiteFooterFullMobile />
    </div>
  );
}

// eslint-disable max-len
function SiteFooter() {
  const siteConfig = useSelector((state) => state.siteConfig);

  if (siteConfig.showDistractions) {
    return (
      <footer className='bg-neutral px-4 py-12 md:p-20'>
        <SiteFooterFull />
      </footer>
    );
  }

  return (
    <footer className='bg-neutral px-4 py-12 md:px-20'>
      <div className='mx-auto max-w-7xl p-0'>
        <Disclaimer />
        <div className='flex flex-row flex-wrap justify-start gap-7 [&_a]:text-base'>
          <FooterLink
            to='https://about.voteamerica.org/privacy'
            text='Privacy'
            title='VoteAmerica Privacy Policy'
          />
          <FooterLink
            to='https://about.voteamerica.org/terms'
            text='Terms'
            title='VoteAmerica Terms of Service'
          />
          <FooterLink
            to='https://about.voteamerica.org/contact'
            text='Contact'
            title='VoteAmerica Contact Info'
          />
        </div>
      </div>
    </footer>
  );
}

export default SiteFooter;
